import type { CSSProperties } from "react";
import type { DrawAssignment, Ladder, LadderRow } from "@/lib/draw";
import { tracePath } from "@/lib/draw";
import { pathTiming, type RevealTiming } from "@/lib/reveal";

// 칸 폭·줄 높이. 손가락으로 따라갈 수 있어야 해서 좁히지 않는다 — 사람이 많으면
// 폭을 줄이는 대신 가로 스크롤로 넘긴다.
const COL = 52;
const ROW = 30;
const TOP = 12;

/** 사다리 한 판. 연출 중에는 경로가 순서대로 내려가고, **선이 바닥에 닿는 순간**
 *  그 칸이 열린다 — 경로와 결과가 한 시계에서 나와 인과가 보인다.
 *
 *  연출이 끝나면 이름을 눌러 특정 경로만 다시 볼 수 있다. */
export function LadderBoard({
  assignments,
  ladder,
  outcomes,
  timing,
  /** 공개된 개수. `revealAt` 이 준 값이다. */
  revealed,
  /** false 면 전부 열린 상태로 그린다(`prefers-reduced-motion`, `바로 보기`). */
  playing,
  highlight,
  onPickColumn,
}: {
  assignments: DrawAssignment[];
  ladder: Ladder;
  outcomes: string[];
  timing: RevealTiming;
  revealed: number;
  playing: boolean;
  /** 연출이 끝난 뒤 다시 보기로 고른 출발 칸. */
  highlight: number | null;
  onPickColumn: (start: number) => void;
}) {
  const size = assignments.length;
  const width = size * COL;
  // 가로줄은 1..rows.length 줄에 놓이므로 기둥은 한 칸 더 내려가야 한다 — 안 그러면
  // 마지막 가로줄이 기둥 끝에 딱 붙어서 결과 칸으로 내려가는 선이 안 보인다.
  const rows: LadderRow[] = ladder.rows;
  const bottom = rows.length + 1;
  const height = TOP * 2 + bottom * ROW;
  const centerOf = (column: number) => column * COL + COL / 2;
  const yOf = (step: number) => TOP + step * ROW;

  /** 연출 중에는 아직 안 내려온 경로까지 다 그린다(애니메이션이 순서를 만든다).
   *  끝난 뒤에는 고른 하나만 그린다. */
  const drawnStarts = playing
    ? assignments.map((_, start) => start)
    : highlight === null
      ? []
      : [highlight];

  function pointsOf(start: number): string {
    const path = tracePath(rows, start);
    return path
      .flatMap((column, step) =>
        step === 0
          ? [`${centerOf(column)},${yOf(0)}`]
          : [`${centerOf(path[step - 1])},${yOf(step)}`, `${centerOf(column)},${yOf(step)}`]
      )
      .concat(`${centerOf(path[path.length - 1])},${yOf(bottom)}`)
      .join(" ");
  }

  const landedColumns = new Set(ladder.permutation.slice(0, revealed));
  const highlightLanding = highlight === null ? null : ladder.permutation[highlight];

  return (
    // 사람이 많으면 가로로 넘긴다. 페이지 본문은 절대 좌우로 흐르지 않는다.
    <div className="overflow-x-auto">
      <div style={{ width, minWidth: "100%" }}>
        <ul className="flex" style={{ width }}>
          {assignments.map((a, column) => (
            <li key={a.name} style={{ width: COL }} className="min-w-0 px-0.5 text-center">
              <button
                type="button"
                // 연출 중에는 순서를 건드리지 못하게 잠근다.
                disabled={playing}
                // 눌러서 켜고 끄는 토글이다 — 화면 낭독기에도 그렇게 들려야 한다.
                aria-pressed={highlight === column}
                onClick={() => onPickColumn(column)}
                // 칸 폭(52px)은 사다리가 정한다. 높이라도 손가락에 맞춘다.
                className={
                  highlight === column
                    ? "w-full truncate rounded-sm py-2 text-label-2 font-semibold text-primary"
                    : "w-full truncate rounded-sm py-2 text-label-2 text-label-normal"
                }
              >
                {a.name}
              </button>
            </li>
          ))}
        </ul>

        <svg
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label="사다리"
        >
          {assignments.map((a, column) => (
            <line
              key={a.name}
              x1={centerOf(column)}
              y1={yOf(0)}
              x2={centerOf(column)}
              y2={yOf(bottom)}
              className="stroke-line-normal"
              strokeWidth={2}
            />
          ))}
          {rows.map((row, step) =>
            row.map((left) => (
              <line
                key={`${step}-${left}`}
                x1={centerOf(left)}
                y1={yOf(step + 1)}
                x2={centerOf(left + 1)}
                y2={yOf(step + 1)}
                className="stroke-line-strong"
                strokeWidth={2}
              />
            ))
          )}
          {/* 경로는 `tracePath` 가 준 칸 순서를 그대로 잇는다 — 그림과 결과가 같은
              함수에서 나오므로 손가락으로 따라간 것과 어긋날 수 없다.
              지연과 길이는 `pathTiming` 이 주고, 그 합이 곧 `revealAt` 의 확정 시각이다. */}
          {drawnStarts.map((start) => {
            const css = pathTiming(start, timing);
            const landed = !playing || landedColumns.has(ladder.permutation[start]);
            return (
              <polyline
                // 다시 보기로 다른 이름을 누르면 처음부터 그려지게 remount 한다.
                key={`path-${start}`}
                points={pointsOf(start)}
                fill="none"
                // 길이를 100 으로 정규화하면 경로가 얼마나 길든 같은 keyframe 이 쓰인다.
                // 애니메이션이 꺼진 사용자에게는 attribute 값(offset 0)이 그대로 남아
                // 선이 처음부터 다 그려진 상태로 보인다.
                pathLength={100}
                strokeDasharray={100}
                strokeDashoffset={0}
                className={
                  // 이미 도착한 경로는 흐리게 남겨 사다리가 채워지는 느낌을 만든다.
                  // 지금 내려가는 중인 경로만 진하다.
                  landed && playing
                    ? "animate-draw-line stroke-line-strong motion-reduce:animate-none"
                    : "animate-draw-line stroke-primary motion-reduce:animate-none"
                }
                style={
                  playing
                    ? ({
                        animationDelay: `${css.delayMs}ms`,
                        animationDuration: `${css.durationMs}ms`,
                      } as CSSProperties)
                    : undefined
                }
                strokeWidth={3}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            );
          })}
        </svg>

        <ul className="flex" style={{ width }}>
          {assignments.map((_, column) => {
            const open = !playing || landedColumns.has(column);
            const emphasized = highlightLanding === column;
            return (
              <li
                key={`outcome-${column}`}
                style={{ width: COL }}
                className="min-w-0 px-0.5 text-center"
              >
                <span
                  className={
                    open
                      ? emphasized
                        ? "block animate-pop-in truncate text-label-2 font-semibold text-primary"
                        : "block animate-pop-in truncate text-label-2 text-label-normal"
                      : // 아직 선이 안 닿았다. 닫힌 칸은 값을 보여주지 않는다 —
                        // 미리 보이면 선이 내려가는 걸 볼 이유가 없어진다.
                        "block truncate text-label-2 text-label-assistive"
                  }
                >
                  {open ? outcomes[column] : "?"}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
